import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { displayDuration, getDuration } from 'utils';
import { LoggerService } from 'services';
import { Card, MembersOnlyBadge } from 'components';
import { CameraIcon, PlusIcon } from 'components/Icon';

import styles from './EventCard.Upcoming.module.scss';

export type UpcomingEventCardProps = {
  eventCode: string;
  eventName: string;
  startDate: string;
  channelName: string;
  isMembersOnly?: boolean;
  hasAccess?: boolean;
  poster?: string;
  onAddToCalendar?(): Promise<void>;
  onClick?(): void;
};

const displayDate = (date: string) => dayjs(date).format('DD MMM');
const displayTime = (date: string) => dayjs(date).format('hh:mm A');

const Duration = memo<{ overdue?: boolean; startDate: string }>(({ overdue, startDate }) => {
  const [duration, setDuration] = useState(() => getDuration(startDate));

  useEffect(() => {
    const durationInterval = setInterval(() => {
      setDuration(() => getDuration(startDate));
    }, 1000);

    return () => {
      clearInterval(durationInterval);
    };
  }, [startDate]);

  if (overdue) {
    return <span className={styles.duration}>Starts soon</span>;
  }

  return (
    <span className={styles.duration}>
      Starts in <b>{displayDuration(duration)}</b>
    </span>
  );
});

const UpcomingEventCard = ({
  eventCode,
  eventName,
  poster,
  startDate,
  channelName,
  isMembersOnly = false,
  hasAccess = false,
  onAddToCalendar,
  onClick
}: UpcomingEventCardProps): JSX.Element => {
  const [posterLoadFailed, setPosterLoadFailed] = useState(!poster);
  const [isGeneratingCalendarFile, setGeneratingCalendarFile] = useState(false);

  const overdue = useMemo(() => new Date() > new Date(startDate), [startDate]);
  const startsSoon = useMemo(() => getDuration(startDate) <= 1000 * 60 * 30, [startDate]);

  const handleOnPosterLoadFailed = useCallback(() => setPosterLoadFailed(true), []);

  const handleOnAddToCalendar = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();

    try {
      setGeneratingCalendarFile(true);

      await onAddToCalendar?.();
    } catch (error) {
      LoggerService.log('ADD_TO_CALENDAR', error);
    } finally {
      setGeneratingCalendarFile(false);
    }
  };

  const handleOnClick = () => {
    onClick?.();
  };

  return (
    <Card className={styles.container} onClick={handleOnClick}>
      <div className={styles.poster}>
        <div className={styles.posterContent}>
          {!overdue && !startsSoon ? (
            <>
              <div className={styles.date}>{displayDate(startDate)}</div>
              <div className={styles.time}>{displayTime(startDate)}</div>
            </>
          ) : (
            <Duration overdue={overdue} startDate={startDate} />
          )}
        </div>

        {posterLoadFailed || !poster ? (
          <div className={styles.placeholder}>
            <CameraIcon />
          </div>
        ) : (
          <img className={styles.image} src={poster} alt={eventName} onError={handleOnPosterLoadFailed} />
        )}

        {isMembersOnly && <MembersOnlyBadge className={styles.membersOnlyBadge} hasAccess={hasAccess} />}
      </div>

      <div className={styles.content}>
        <div className={styles.title}>{eventName}</div>
        <div className={styles.channel}>@{channelName}</div>
        <button
          disabled={isGeneratingCalendarFile}
          type="button"
          className={styles.cta}
          onClick={handleOnAddToCalendar}
        >
          <PlusIcon className="mr-4" />
          <span>Add to calendar</span>
        </button>
      </div>
    </Card>
  );
};

export default memo(UpcomingEventCard);
